import { createContext, useContext, useState, useEffect, type ReactNode } from "react"
import { api } from "./api"

type Language = "uz" | "en" | "ru"

interface LanguageOption {
    code: Language
    name: string
}

interface SiteSettings {
    site_name: string
    logo_url: string
    email: string
    phone: string
    address: string
    instagram_url: string
    telegram_url: string
    facebook_url: string
    location_url: string
}

interface LanguageContextType {
    currentLang: Language
    setCurrentLang: (lang: Language) => void
    t: (section: string, key: string) => string
    siteSettings: SiteSettings
    languages: LanguageOption[]
}

const languages: LanguageOption[] = [
    { code: "uz", name: "O'zbek" },
    { code: "en", name: "English" },
    { code: "ru", name: "Русский" },
]

const defaultSiteSettings: SiteSettings = {
    site_name: "TabiatShop",
    logo_url: "",
    email: "info@tabiatshop.uz",
    phone: "+998 90 123 45 67",
    address: "Toshkent, O'zbekiston",
    instagram_url: "",
    telegram_url: "",
    facebook_url: "",
    location_url: "",
}

const allDefaultTranslations: Record<Language, Record<string, Record<string, string>>> = {
    uz: {
        nav: {
            home: "Bosh sahifa",
            products: "Mahsulotlar",
            categories: "Kategoriyalar",
            about: "Biz haqimizda",
            contact: "Aloqa",
        },
        header: {
            home: "Bosh sahifa",
            products: "Mahsulotlar",
            categories: "Kategoriyalar",
            about: "Biz haqimizda",
            contact: "Aloqa",
            search_placeholder: "Mahsulot qidirish...",
            working_hours: "Ish vaqti: 9:00 - 21:00",
            free_delivery: "100,000 so'mdan bepul yetkazib berish",
            tagline: "Tabiiy mahsulotlar",
        },
        home: {
            // Hero Section
            hero_badge: "Premium sifat kafolati",
            hero_title_1: "Tabiat Kuchi Bilan",
            hero_title_2: "Sog'lom Hayot",
            hero_description:
                "O'zbekistonning eng ishonchli tabiiy dori-darmonlar va sog'liqni saqlash mahsulotlari do'koni. 15 yillik tajriba va 100,000+ mamnun mijozlar.",
            hero_cta_primary: "Xarid qilish",
            hero_cta_secondary: "Batafsil",

            // Trust Bar
            trust_natural: "100% Tabiiy",
            trust_certified: "Sertifikatlangan",
            trust_delivery: "Tez yetkazib berish",
            trust_support: "24/7 Qo'llab-quvvatlash",
            trust_shipping: "Bepul yetkazib berish",
            trust_quality: "Yuqori sifat",

            // Categories Section
            categories_badge: "Kategoriyalar",
            categories_title: "Mahsulot Kategoriyalari",
            categories_subtitle: "Sog'liq va go'zallik uchun eng yaxshi tabiiy mahsulotlar",
            view_category: "Ko'rish",
            no_categories: "Kategoriyalar mavjud emas",
            no_categories_desc: "Hozircha kategoriyalar qo'shilmagan",

            // Products Section
            products_badge: "Mashhur mahsulotlar",
            products_title: "Eng Ko'p Sotiladigan Mahsulotlar",
            products_subtitle: "Mijozlarimiz eng ko'p tanlaydigan sifatli mahsulotlar",
            no_products: "Mahsulotlar mavjud emas",
            no_products_desc: "Hozircha mahsulotlar qo'shilmagan",
            free_delivery: "100,000 so'mdan bepul yetkazib berish",
            guarantee: "Sifat kafolati",
            fast_delivery: "Tez yetkazib berish",

            // About Section
            about_badge: "Biz haqimizda",
            about_title: "Tabiat Inʼomlaridan Sog'liqqa Yo'l",
            about_text:
                "Biz 2009-yildan beri O'zbekiston bo'ylab tabiiy dori-darmonlar va sog'liqni saqlash mahsulotlarini ishlab chiqarish va yetkazib berish bilan shug'ullanamiz. Bizning maqsadimiz - har bir oilaga sifatli va arzon narxlardagi tabiiy mahsulotlarni yetkazish.",
            about_feature_1: "100% tabiiy ingredientlar",
            about_feature_2: "Xalqaro sertifikatlar",
            about_feature_3: "15+ yillik tajriba",
            about_feature_4: "Barcha viloyatlarga yetkazib berish",
            about_experience: "Yillik tajriba",
            about_cta: "Batafsil ma'lumot",

            // Benefits Section
            benefits_badge: "Afzalliklarimiz",
            benefits_title: "Nega Aynan Biz?",
            benefits_subtitle: "Mijozlarimiz bizni tanlashining asosiy sabablari",
            benefit_1_title: "Tabiiy Mahsulotlar",
            benefit_1_desc: "Faqat sertifikatlangan tabiiy ingredientlardan foydalanamiz",
            benefit_2_title: "Tez Yetkazib Berish",
            benefit_2_desc: "Buyurtmangizni 24 soat ichida yetkazib beramiz",
            benefit_3_title: "Sifat Kafolati",
            benefit_3_desc: "Barcha mahsulotlar sifat nazoratidan o'tgan",
            benefit_4_title: "Qulay Narxlar",
            benefit_4_desc: "Eng yaxshi sifat va qulay narxlar kombinatsiyasi",

            // Stats Section
            stats_badge: "Raqamlarda",
            stats_title: "Bizning Yutuqlarimiz",
            stats_subtitle: "15 yillik mehnat natijalari",
            stat_years: "Yillik tajriba",
            stat_products: "Mahsulot turi",
            stat_customers: "Mamnun mijoz",
            stat_regions: "Viloyat",
            stat_experience: "Yillik tajriba",
            stat_reviews: "Ijobiy sharhlar",

            // Process Section
            process_badge: "Jarayon",
            process_title: "Qanday Ishlaymiz",
            process_subtitle: "Buyurtma berishdan yetkazib berishgacha",
            process_1_title: "Buyurtma",
            process_1_desc: "Sayt orqali yoki telefon orqali buyurtma bering",
            process_2_title: "Tasdiqlash",
            process_2_desc: "Operatorimiz buyurtmangizni tasdiqlaydi",
            process_3_title: "Yig'ish",
            process_3_desc: "Mahsulotlarni ehtiyotkorlik bilan yig'amiz",
            process_4_title: "Yetkazish",
            process_4_desc: "Tez va xavfsiz yetkazib beramiz",

            // Testimonials Section
            testimonials_badge: "Mijozlar fikri",
            testimonials_title: "Mijozlarimiz Nima Deyishadi",
            testimonials_subtitle: "Minglab mamnun mijozlarimizning fikrlari",
            testimonial_1_text:
                "TabiatShop mahsulotlaridan juda mamnunman. Tabiiy asal va qora sedana sog'ligimga juda yaxshi ta'sir ko'rsatdi. Rahmat!",
            testimonial_1_name: "Aziza Karimova",
            testimonial_1_role: "Doimiy mijoz",
            testimonial_2_text:
                "15 yildan beri shu do'kondan xarid qilaman. Mahsulotlar sifati doimo yuqori darajada. Yetkazib berish ham juda tez.",
            testimonial_2_name: "Bobur Aliyev",
            testimonial_2_role: "Tadbirkor",
            testimonial_3_text:
                "Onam uchun dori-darmonlar oldim. Natijalar ajoyib! Endi butun oilamiz TabiatShop mijozlariga aylandi.",
            testimonial_3_name: "Malika Rahimova",
            testimonial_3_role: "Shifokor",

            // FAQ Section
            faq_badge: "Savol-javob",
            faq_title: "Ko'p Beriladigan Savollar",
            faq_subtitle: "Eng ko'p so'raladigan savollarga javoblar",
            faq_1_q: "Mahsulotlar sertifikatlangan mi?",
            faq_1_a: "Ha, barcha mahsulotlarimiz tegishli davlat organlaridan sertifikat olgan va sifat nazoratidan o'tgan.",
            faq_2_q: "Yetkazib berish qancha vaqt oladi?",
            faq_2_a: "Toshkent shahri bo'ylab 1-2 soat, viloyatlarga 1-3 kun ichida yetkazib beramiz.",
            faq_3_q: "To'lov qanday amalga oshiriladi?",
            faq_3_a: "Naqd pul, bank kartasi yoki Click/Payme orqali to'lash mumkin.",
            faq_4_q: "Mahsulotni qaytarish mumkinmi?",
            faq_4_a: "Ha, 14 kun ichida ochilmagan mahsulotni qaytarish mumkin.",

            // Newsletter Section
            newsletter_badge: "Yangiliklar",
            newsletter_title: "Yangiliklardan Xabardor Bo'ling",
            newsletter_text: "Chegirmalar va maxsus takliflardan birinchi bo'lib xabardor bo'ling",
            newsletter_desc:
                "Email manzilingizni qoldiring va eng so'nggi yangiliklar, maxsus takliflar va chegirmalardan xabardor bo'ling.",
            newsletter_placeholder: "Email manzilingiz",
            newsletter_button: "Obuna bo'lish",

            // CTA Section
            cta_title: "Sog'lom Hayotga Birinchi Qadam",
            cta_subtitle: "Bugun buyurtma bering va tabiiy mahsulotlarimiz sifatini his qiling",
            cta_button: "Xarid qilish",
            cta_call: "Qo'ng'iroq qiling",
            cta_text: "Tabiiy mahsulotlarimiz bilan sog'lom hayotga qadam qo'ying",

            // Delivery info
            delivery_24h: "24 soat ichida yetkazib berish",
            certified: "Sertifikatlangan mahsulotlar",

            // Additional translations
            featured_categories: "Mashhur kategoriyalar",
            featured_products: "Mashhur mahsulotlar",
            categories_view_all: "Barcha kategoriyalar",
            products_view_all: "Barcha mahsulotlar",

            // Product card translations
            order_button: "Buyurtma berish",
            currency: "so'm",
            in_stock: "dona mavjud",
            out_of_stock: "Sotuvda yo'q",
            low_stock: "Kam qoldi",
            reviews: "ta sharh",
            products_count: "ta mahsulot",
        },
        categories: {
            title: "Kategoriyalar",
            subtitle: "Sog'liq va go'zallik uchun eng yaxshi tabiiy mahsulotlar",
            explore_categories: "Kategoriyalarni o'rganing",
            products_count: "ta mahsulot",
            view_products: "Ko'rish",
            no_categories: "Kategoriyalar topilmadi",
        },
        order: {
            order_modal_title: "Buyurtma berish",
            order_name: "Ismingiz",
            order_phone: "Telefon raqamingiz",
            order_quantity: "Miqdori",
            order_cancel: "Bekor qilish",
            order_submit: "Buyurtma berish",
            order_success: "Buyurtmangiz qabul qilindi!",
            order_success_message: "Tez orada operatorimiz siz bilan bog'lanadi",
        },
        products: {
            all_categories: "Barcha kategoriyalar",
            price_range: "Narx oralig'i",
            filters: "Filtrlar",
            new_only: "Faqat yangilar",
            popular_only: "Faqat mashhurlar",
            sort_by: "Saralash",
            sort_newest: "Eng yangi",
            sort_price_low: "Arzon - Qimmat",
            sort_price_high: "Qimmat - Arzon",
            sort_name_az: "A - Z",
            sort_name_za: "Z - A",
            no_products: "Mahsulotlar topilmadi",
            try_different_filter: "Boshqa filtrlarni sinab ko'ring",
            clear_filters: "Filtrlarni tozalash",
            items: "ta mahsulot",
            in_stock: "dona mavjud",
            out_of_stock: "Sotuvda yo'q",
            low_stock: "Kam qoldi",
            order_button: "Buyurtma berish",
            currency: "so'm",
            reviews: "ta sharh",
            products_count: "ta mahsulot",
        },
        footer: {
            description: "Tabiiy mahsulotlar do'koni. Sog'liq va go'zallik uchun eng yaxshi mahsulotlar.",
            quick_links: "Tezkor havolalar",
            contact_us: "Biz bilan bog'laning",
            follow_us: "Bizni kuzating",
            copyright: "Barcha huquqlar himoyalangan",
            privacy_policy: "Maxfiylik siyosati",
            terms: "Foydalanish shartlari",
            // Newsletter
            newsletter_title: "Yangiliklardan xabardor bo'ling",
            newsletter_desc: "Maxsus takliflar va chegirmalardan birinchi bo'lib xabardor bo'ling",
            newsletter_placeholder: "Email manzilingiz",
            newsletter_button: "Obuna bo'lish",
            // Customer service
            customer_service: "Mijozlarga xizmat",
            shipping_info: "Yetkazib berish",
            returns: "Qaytarish",
            faq: "Savol-javob",
            privacy: "Maxfiylik",
            // Contact
            contact: "Aloqa",
            phone_label: "Telefon",
            email_label: "Email",
            address_label: "Manzil",
            // Trust badges
            certified: "Sertifikatlangan",
            delivery_24h: "24 soatda yetkazish",
            open_in_maps: "Xaritada ochish",
        },
        contact: {
            info_title: "Bizning joylashuvimiz",
        },
    },
    en: {
        nav: {
            home: "Home",
            products: "Products",
            categories: "Categories",
            about: "About Us",
            contact: "Contact",
        },
        header: {
            home: "Home",
            products: "Products",
            categories: "Categories",
            about: "About Us",
            contact: "Contact",
            search_placeholder: "Search products...",
            working_hours: "Working hours: 9:00 - 21:00",
            free_delivery: "Free delivery from 100,000 UZS",
            tagline: "Natural products",
        },
        home: {
            hero_badge: "Premium Quality Guaranteed",
            hero_title_1: "Healthy Life With",
            hero_title_2: "Nature's Power",
            hero_description:
                "Uzbekistan's most trusted natural medicines and health products store. 15 years of experience and 100,000+ satisfied customers.",
            hero_cta_primary: "Shop Now",
            hero_cta_secondary: "Learn More",

            trust_natural: "100% Natural",
            trust_certified: "Certified",
            trust_delivery: "Fast Delivery",
            trust_support: "24/7 Support",
            trust_shipping: "Free Shipping",
            trust_quality: "High Quality",

            categories_badge: "Categories",
            categories_title: "Product Categories",
            categories_subtitle: "Best natural products for health and beauty",
            view_category: "View",
            no_categories: "No categories",
            no_categories_desc: "No categories have been added yet",

            products_badge: "Popular Products",
            products_title: "Best Selling Products",
            products_subtitle: "Quality products most chosen by our customers",
            no_products: "No products",
            no_products_desc: "No products have been added yet",
            free_delivery: "Free delivery from 100,000 UZS",
            guarantee: "Quality Guarantee",
            fast_delivery: "Fast Delivery",

            about_badge: "About Us",
            about_title: "Path to Health from Nature's Gifts",
            about_text:
                "Since 2009, we have been producing and delivering natural medicines and health products throughout Uzbekistan. Our goal is to deliver quality and affordable natural products to every family.",
            about_feature_1: "100% natural ingredients",
            about_feature_2: "International certificates",
            about_feature_3: "15+ years experience",
            about_feature_4: "Delivery to all regions",
            about_experience: "Years of experience",
            about_cta: "Learn More",

            benefits_badge: "Our Advantages",
            benefits_title: "Why Choose Us?",
            benefits_subtitle: "Main reasons why our customers choose us",
            benefit_1_title: "Natural Products",
            benefit_1_desc: "We only use certified natural ingredients",
            benefit_2_title: "Fast Delivery",
            benefit_2_desc: "We deliver your order within 24 hours",
            benefit_3_title: "Quality Guarantee",
            benefit_3_desc: "All products have passed quality control",
            benefit_4_title: "Affordable Prices",
            benefit_4_desc: "Best combination of quality and price",

            stats_badge: "In Numbers",
            stats_title: "Our Achievements",
            stats_subtitle: "Results of 15 years of work",
            stat_years: "Years Experience",
            stat_products: "Product Types",
            stat_customers: "Happy Customers",
            stat_regions: "Regions",
            stat_experience: "Years of experience",
            stat_reviews: "Positive reviews",

            process_badge: "Process",
            process_title: "How We Work",
            process_subtitle: "From order to delivery",
            process_1_title: "Order",
            process_1_desc: "Place order via website or phone",
            process_2_title: "Confirmation",
            process_2_desc: "Our operator confirms your order",
            process_3_title: "Packaging",
            process_3_desc: "We carefully pack the products",
            process_4_title: "Delivery",
            process_4_desc: "Fast and safe delivery",

            testimonials_badge: "Testimonials",
            testimonials_title: "What Our Customers Say",
            testimonials_subtitle: "Reviews from thousands of satisfied customers",
            testimonial_1_text:
                "I am very satisfied with TabiatShop products. Natural honey and black seed had a great effect on my health. Thank you!",
            testimonial_1_name: "Aziza Karimova",
            testimonial_1_role: "Regular customer",
            testimonial_2_text:
                "I have been shopping from this store for 15 years. Product quality is always top-notch. Delivery is also very fast.",
            testimonial_2_name: "Bobur Aliyev",
            testimonial_2_role: "Entrepreneur",
            testimonial_3_text:
                "I bought medicines for my mother. The results are amazing! Now our whole family has become TabiatShop customers.",
            testimonial_3_name: "Malika Rahimova",
            testimonial_3_role: "Doctor",

            faq_badge: "FAQ",
            faq_title: "Frequently Asked Questions",
            faq_subtitle: "Answers to the most common questions",
            faq_1_q: "Are the products certified?",
            faq_1_a: "Yes, all our products are certified by relevant government agencies and have passed quality control.",
            faq_2_q: "How long does delivery take?",
            faq_2_a: "Within Tashkent city 1-2 hours, to regions 1-3 days.",
            faq_3_q: "How is payment made?",
            faq_3_a: "You can pay with cash, bank card or via Click/Payme.",
            faq_4_q: "Can I return a product?",
            faq_4_a: "Yes, unopened products can be returned within 14 days.",

            newsletter_badge: "Newsletter",
            newsletter_title: "Stay Updated",
            newsletter_text: "Be the first to know about discounts and special offers",
            newsletter_desc: "Leave your email address to stay informed about latest news, special offers and discounts.",
            newsletter_placeholder: "Your email address",
            newsletter_button: "Subscribe",

            cta_title: "First Step to Healthy Life",
            cta_subtitle: "Order today and feel the quality of our natural products",
            cta_button: "Shop Now",
            cta_call: "Call Us",
            cta_text: "Take a step towards a healthy life with our natural products",

            delivery_24h: "24-hour delivery",
            certified: "Certified products",

            featured_categories: "Featured Categories",
            featured_products: "Featured Products",
            categories_view_all: "All Categories",
            products_view_all: "All Products",

            // Product card translations
            order_button: "Order Now",
            currency: "UZS",
            in_stock: "in stock",
            out_of_stock: "Out of stock",
            low_stock: "Low stock",
            reviews: "reviews",
            products_count: "products",
        },
        categories: {
            title: "Categories",
            subtitle: "Best natural products for health and beauty",
            explore_categories: "Explore Categories",
            products_count: "products",
            view_products: "View Products",
            no_categories: "No categories found",
        },
        order: {
            order_modal_title: "Place Order",
            order_name: "Your Name",
            order_phone: "Phone Number",
            order_quantity: "Quantity",
            order_cancel: "Cancel",
            order_submit: "Place Order",
            order_success: "Your order has been placed!",
            order_success_message: "Our operator will contact you soon",
        },
        products: {
            all_categories: "All Categories",
            price_range: "Price Range",
            filters: "Filters",
            new_only: "New Only",
            popular_only: "Popular Only",
            sort_by: "Sort By",
            sort_newest: "Newest",
            sort_price_low: "Price: Low to High",
            sort_price_high: "Price: High to Low",
            sort_name_az: "A - Z",
            sort_name_za: "Z - A",
            no_products: "No products found",
            try_different_filter: "Try different filters",
            clear_filters: "Clear Filters",
            items: "products",
            in_stock: "in stock",
            out_of_stock: "Out of stock",
            low_stock: "Low stock",
            order_button: "Order Now",
            currency: "UZS",
            reviews: "reviews",
            products_count: "products",
        },
        footer: {
            description: "Natural products store. The best products for health and beauty.",
            quick_links: "Quick Links",
            contact_us: "Contact Us",
            follow_us: "Follow Us",
            copyright: "All rights reserved",
            privacy_policy: "Privacy Policy",
            terms: "Terms of Service",
            // Newsletter
            newsletter_title: "Stay Updated",
            newsletter_desc: "Be the first to know about special offers and discounts",
            newsletter_placeholder: "Your email address",
            newsletter_button: "Subscribe",
            // Customer service
            customer_service: "Customer Service",
            shipping_info: "Shipping Info",
            returns: "Returns",
            faq: "FAQ",
            privacy: "Privacy",
            // Contact
            contact: "Contact",
            phone_label: "Phone",
            email_label: "Email",
            address_label: "Address",
            // Trust badges
            certified: "Certified",
            delivery_24h: "24h Delivery",
            open_in_maps: "Open in Maps",
        },
        contact: {
            info_title: "Our Location",
        },
    },
    ru: {
        nav: {
            home: "Главная",
            products: "Продукты",
            categories: "Категории",
            about: "О нас",
            contact: "Контакты",
        },
        header: {
            home: "Главная",
            products: "Продукты",
            categories: "Категории",
            about: "О нас",
            contact: "Контакты",
            search_placeholder: "Поиск продуктов...",
            working_hours: "Время работы: 9:00 - 21:00",
            free_delivery: "Бесплатная доставка от 100 000 сум",
            tagline: "Натуральные продукты",
        },
        home: {
            hero_badge: "Гарантия премиум качества",
            hero_title_1: "Здоровая Жизнь с",
            hero_title_2: "Силой Природы",
            hero_description:
                "Самый надежный магазин натуральных лекарств и товаров для здоровья в Узбекистане. 15 лет опыта и более 100 000 довольных клиентов.",
            hero_cta_primary: "Купить",
            hero_cta_secondary: "Подробнее",

            trust_natural: "100% Натуральные",
            trust_certified: "Сертифицировано",
            trust_delivery: "Быстрая доставка",
            trust_support: "Поддержка 24/7",
            trust_shipping: "Бесплатная доставка",
            trust_quality: "Высокое качество",

            categories_badge: "Категории",
            categories_title: "Категории Продуктов",
            categories_subtitle: "Лучшие натуральные продукты для здоровья и красоты",
            view_category: "Смотреть",
            no_categories: "Нет категорий",
            no_categories_desc: "Пока не добавлено ни одной категории",

            products_badge: "Популярные товары",
            products_title: "Самые Продаваемые Товары",
            products_subtitle: "Качественные товары, которые чаще всего выбирают наши клиенты",
            no_products: "Нет товаров",
            no_products_desc: "Пока не добавлено ни одного товара",
            free_delivery: "Бесплатная доставка от 100 000 сум",
            guarantee: "Гарантия качества",
            fast_delivery: "Быстрая доставка",

            about_badge: "О нас",
            about_title: "Путь к Здоровью от Даров Природы",
            about_text:
                "С 2009 года мы производим и доставляем натуральные лекарства и товары для здоровья по всему Узбекистану. Наша цель - доставить качественные и доступные натуральные продукты каждой семье.",
            about_feature_1: "100% натуральные ингредиенты",
            about_feature_2: "Международные сертификаты",
            about_feature_3: "15+ лет опыта",
            about_feature_4: "Доставка во все регионы",
            about_experience: "Лет опыта",
            about_cta: "Узнать больше",

            benefits_badge: "Наши преимущества",
            benefits_title: "Почему Выбирают Нас?",
            benefits_subtitle: "Основные причины, почему наши клиенты выбирают нас",
            benefit_1_title: "Натуральные Продукты",
            benefit_1_desc: "Мы используем только сертифицированные натуральные ингредиенты",
            benefit_2_title: "Быстрая Доставка",
            benefit_2_desc: "Доставляем ваш заказ в течение 24 часов",
            benefit_3_title: "Гарантия Качества",
            benefit_3_desc: "Все продукты прошли контроль качества",
            benefit_4_title: "Доступные Цены",
            benefit_4_desc: "Лучшее сочетание качества и цены",

            stats_badge: "В цифрах",
            stats_title: "Наши Достижения",
            stats_subtitle: "Результаты 15 лет работы",
            stat_years: "Лет опыта",
            stat_products: "Видов продукции",
            stat_customers: "Довольных клиентов",
            stat_regions: "Регионов",
            stat_experience: "Лет опыта",
            stat_reviews: "Положительных отзывов",

            process_badge: "Процесс",
            process_title: "Как Мы Работаем",
            process_subtitle: "От заказа до доставки",
            process_1_title: "Заказ",
            process_1_desc: "Оформите заказ через сайт или по телефону",
            process_2_title: "Подтверждение",
            process_2_desc: "Наш оператор подтвердит ваш заказ",
            process_3_title: "Упаковка",
            process_3_desc: "Тщательно упаковываем товары",
            process_4_title: "Доставка",
            process_4_desc: "Быстрая и безопасная доставка",

            testimonials_badge: "Отзывы",
            testimonials_title: "Что Говорят Наши Клиенты",
            testimonials_subtitle: "Отзывы тысяч довольных клиентов",
            testimonial_1_text:
                "Я очень довольна продукцией TabiatShop. Натуральный мед и черный тмин оказали отличное влияние на мое здоровье. Спасибо!",
            testimonial_1_name: "Азиза Каримова",
            testimonial_1_role: "Постоянный клиент",
            testimonial_2_text:
                "Покупаю в этом магазине уже 15 лет. Качество продукции всегда на высоте. Доставка тоже очень быстрая.",
            testimonial_2_name: "Бобур Алиев",
            testimonial_2_role: "Предприниматель",
            testimonial_3_text:
                "Купила лекарства для мамы. Результаты потрясающие! Теперь вся наша семья стала клиентами TabiatShop.",
            testimonial_3_name: "Малика Рахимова",
            testimonial_3_role: "Врач",

            faq_badge: "FAQ",
            faq_title: "Часто Задаваемые Вопросы",
            faq_subtitle: "Ответы на самые распространенные вопросы",
            faq_1_q: "Продукция сертифицирована?",
            faq_1_a:
                "Да, вся наша продукция сертифицирована соответствующими государственными органами и прошла контроль качества.",
            faq_2_q: "Сколько времени занимает доставка?",
            faq_2_a: "По Ташкенту 1-2 часа, в регионы 1-3 дня.",
            faq_3_q: "Как производится оплата?",
            faq_3_a: "Можно оплатить наличными, банковской картой или через Click/Payme.",
            faq_4_q: "Можно ли вернуть товар?",
            faq_4_a: "Да, невскрытый товар можно вернуть в течение 14 дней.",

            newsletter_badge: "Новости",
            newsletter_title: "Будьте в курсе",
            newsletter_text: "Узнайте первыми о скидках и специальных предложениях",
            newsletter_desc: "Оставьте ваш email, чтобы быть в курсе последних новостей, специальных предложений и скидок.",
            newsletter_placeholder: "Ваш email адрес",
            newsletter_button: "Подписаться",

            cta_title: "Первый Шаг к Здоровой Жизни",
            cta_subtitle: "Закажите сегодня и почувствуйте качество наших натуральных продуктов",
            cta_button: "Купить",
            cta_call: "Позвоните нам",
            cta_text: "Сделайте шаг к здоровой жизни с нашими натуральными продуктами",

            delivery_24h: "Доставка за 24 часа",
            certified: "Сертифицированные продукты",

            featured_categories: "Популярные категории",
            featured_products: "Популярные продукты",
            categories_view_all: "Все категории",
            products_view_all: "Все продукты",

            // Product card translations
            order_button: "Заказать",
            currency: "сум",
            in_stock: "в наличии",
            out_of_stock: "Нет в наличии",
            low_stock: "Мало",
            reviews: "отзывов",
            products_count: "товаров",
        },
        categories: {
            title: "Категории",
            subtitle: "Лучшие натуральные продукты для здоровья и красоты",
            explore_categories: "Изучите категории",
            products_count: "товаров",
            view_products: "Посмотреть",
            no_categories: "Категории не найдены",
        },
        order: {
            order_modal_title: "Оформить заказ",
            order_name: "Ваше имя",
            order_phone: "Номер телефона",
            order_quantity: "Количество",
            order_cancel: "Отмена",
            order_submit: "Заказать",
            order_success: "Ваш заказ принят!",
            order_success_message: "Наш оператор скоро свяжется с вами",
        },
        products: {
            all_categories: "Все категории",
            price_range: "Диапазон цен",
            filters: "Фильтры",
            new_only: "Только новые",
            popular_only: "Только популярные",
            sort_by: "Сортировка",
            sort_newest: "Новые",
            sort_price_low: "Дешевле - Дороже",
            sort_price_high: "Дороже - Дешевле",
            sort_name_az: "А - Я",
            sort_name_za: "Я - А",
            no_products: "Товары не найдены",
            try_different_filter: "Попробуйте другие фильтры",
            clear_filters: "Очистить фильтры",
            items: "товаров",
            in_stock: "в наличии",
            out_of_stock: "Нет в наличии",
            low_stock: "Мало",
            order_button: "Заказать",
            currency: "сум",
            reviews: "отзывов",
            products_count: "товаров",
        },
        footer: {
            description: "Магазин натуральных продуктов. Лучшие товары для здоровья и красоты.",
            quick_links: "Быстрые ссылки",
            contact_us: "Свяжитесь с нами",
            follow_us: "Мы в соцсетях",
            copyright: "Все права защищены",
            privacy_policy: "Политика конфиденциальности",
            terms: "Условия использования",
            // Newsletter
            newsletter_title: "Будьте в курсе новостей",
            newsletter_desc: "Узнайте первыми о специальных предложениях и скидках",
            newsletter_placeholder: "Ваш email адрес",
            newsletter_button: "Подписаться",
            // Customer service
            customer_service: "Служба поддержки",
            shipping_info: "Доставка",
            returns: "Возврат",
            faq: "Вопросы и ответы",
            privacy: "Конфиденциальность",
            // Contact
            contact: "Контакты",
            phone_label: "Телефон",
            email_label: "Email",
            address_label: "Адрес",
            // Trust badges
            certified: "Сертифицировано",
            delivery_24h: "Доставка за 24ч",
            open_in_maps: "Открыть на карте",
        },
        contact: {
            info_title: "Наше местоположение",
        },
    },
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

export function LanguageProvider({ children }: { children: ReactNode }) {
    const [currentLang, setCurrentLang] = useState<Language>("uz")
    const [translations, setTranslations] = useState(allDefaultTranslations[currentLang])
    const [siteSettings, setSiteSettings] = useState<SiteSettings>(defaultSiteSettings)

    useEffect(() => {
        const savedLang = localStorage.getItem("language") as Language
        if (savedLang && ["uz", "en", "ru"].includes(savedLang)) {
            setCurrentLang(savedLang)
            setTranslations(allDefaultTranslations[savedLang])
        }
    }, [])

    useEffect(() => {
        localStorage.setItem("language", currentLang)
        setTranslations(allDefaultTranslations[currentLang])

        const fetchData = async () => {
            try {
                const [translationsData, settingsData] = await Promise.all([
                    api.getTranslations(currentLang),
                    api.getSiteSettings(),
                ])
                if (translationsData && Object.keys(translationsData).length > 0) {
                    setTranslations((prev) => ({ ...prev, ...translationsData } as Record<string, Record<string, string>>))
                }
                if (settingsData) {
                    setSiteSettings(settingsData as unknown as SiteSettings)
                }
            } catch (error) {
                console.error("Error fetching data:", error)
            }
        }
        fetchData()
    }, [currentLang])

    const t = (section: string, key: string): string => {
        return translations[section]?.[key] || key
    }

    return (
        <LanguageContext.Provider value={{ currentLang, setCurrentLang, t, siteSettings, languages }}>
            {children}
        </LanguageContext.Provider>
    )
}

export function useLanguage() {
    const context = useContext(LanguageContext)
    if (context === undefined) {
        throw new Error("useLanguage must be used within a LanguageProvider")
    }
    return context
}
